import { useCallback } from 'react';

import { FileRejection } from 'react-dropzone';
import isArray from 'lodash/isArray';
import isEmpty from 'lodash/isEmpty';
import fileExtension from 'file-extension';

import { isIE11 } from '../../utils/utils-browser';
import { useTranslation } from 'react-i18next';

type UseDropOptions = {
    readonly onDropFile: (file: File) => void;
    readonly onError: (message: string) => void;
    readonly maxFileSizeInMb: number;
    readonly allowedFileExtensions: ReadonlyArray<string>;
    readonly allowedMIMETypes: ReadonlyArray<string>;
    readonly allowedFileFormatLabel: string;
};

export function useDrop({
    onDropFile,
    onError,
    maxFileSizeInMb,
    allowedFileExtensions,
    allowedMIMETypes,
    allowedFileFormatLabel
}: UseDropOptions) {
    const { t } = useTranslation();
    const onDrop = useCallback(
        (acceptedFiles: File[], rejectedFiles: FileRejection[]) => {
            if (acceptedFiles && isArray(acceptedFiles) && acceptedFiles.length > 0) {
                const acceptedFile = acceptedFiles[0];
                onDropFile(acceptedFile);
            }

            if (rejectedFiles && isArray(rejectedFiles) && rejectedFiles.length > 0) {
                const rejectedFile = rejectedFiles[0];
                //IE 11 fix
                if (isIE11() && isEmpty(rejectedFile.file.type)) {
                    const rejectedFileExtension = fileExtension(rejectedFile.file.name);
                    if (allowedFileExtensions.indexOf(rejectedFileExtension) !== -1) {
                        onDropFile(rejectedFile.file);
                    }
                } else if (allowedMIMETypes.indexOf(rejectedFile.file.type) === -1) {
                    onError(
                        t('yourLocalContent.yourTitles.warnings.uploadWarning', { allowedFileFormatLabel })
                    );
                } else if (rejectedFile.file.size > maxFileSizeInMb * 1024 * 1024) {
                    onError(
                        t('yourLocalContent.yourTitles.warnings.maxSizeWarning', { maxFileSizeInMb })
                    );
                }
            }
        },
        [onDropFile, onError, allowedFileExtensions, allowedFileFormatLabel, allowedMIMETypes, maxFileSizeInMb, t]
    );

    return {
        onDrop
    };
}
